// /account/index — signed-in landing surface. Reads customer from
// AccountProvider; if guest, bounces to /account/login.

import { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAccount } from '../../state/account';
import { ACCOUNT } from '../../constants/testIds';
import OrdersPanel from './OrdersPanel';

export default function AccountIndex() {
        const { status, customer } = useAccount();
        const navigate = useNavigate();
        const location = useLocation();

        useEffect(() => {
                if (status === 'guest') navigate('/account/login', { replace: true });
        }, [status, navigate]);

        useEffect(() => {
                if (status === 'authed' && (location.pathname === '/account/orders' || location.hash === '#orders-panel')) {
                        setTimeout(() => {
                                document.getElementById('orders-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 150);
                }
        }, [status, location.pathname, location.hash]);

        if (status !== 'authed' || !customer) {
                return (
                        <main
                                data-testid={ACCOUNT.indexPage}
                                className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center"
                        >
                                <p className="font-mono text-[12px] tracking-[0.2em] text-white/50 uppercase">
                                        Loading…
                                </p>
                        </main>
                );
        }

        const firstName = (customer.first_name || '').trim();
        // When Shopify has no first_name for the customer, greet by the email
        // local-part instead of the full address — the full address overflows
        // the headline on mobile.
        const displayName = firstName || (customer.email || '').split('@')[0] || 'operator';
        const greeting = displayName.toUpperCase();

        return (
                <main
                        data-testid={ACCOUNT.indexPage}
                        className="min-h-screen bg-[#0A0A0A] text-white px-6 py-16"
                >
                        <div className="mx-auto max-w-[880px]">
                                <p className="font-mono text-[11px] tracking-[0.18em] text-[#FF9F1C]">
                                        LOADOUT / ACCOUNT
                                </p>
                                <h1
                                        data-testid={ACCOUNT.indexGreeting}
                                        className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl leading-none uppercase break-words"
                                >
                                        Hi, {greeting}
                                </h1>
                                <p
                                        data-testid={ACCOUNT.indexEmail}
                                        className="mt-3 font-sans text-[14px] text-white/60"
                                >
                                        {customer.email}
                                </p>

                                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <a
                                                data-testid={ACCOUNT.indexOrdersLink}
                                                href="#orders-panel"
                                                onClick={(e) => {
                                                        e.preventDefault();
                                                        document.getElementById('orders-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                }}
                                                className="group border border-white/15 p-6 flex items-center justify-between hover:border-[#FF9F1C] transition-colors"
                                        >
                                                <div>
                                                        <p className="font-mono text-[11px] tracking-[0.18em] text-white/50 uppercase">
                                                                Orders
                                                        </p>
                                                        <p className="mt-2 font-display text-[22px] uppercase">
                                                                View order history
                                                        </p>
                                                </div>
                                                <span className="font-mono text-[16px] text-[#FF9F1C] group-hover:translate-x-1 transition-transform">
                                                        ↓
                                                </span>
                                        </a>

                                        <Link
                                                to="/"
                                                className="group border border-white/15 p-6 flex items-center justify-between hover:border-[#FF9F1C] transition-colors"
                                        >
                                                <div>
                                                        <p className="font-mono text-[11px] tracking-[0.18em] text-white/50 uppercase">
                                                                Storefront
                                                        </p>
                                                        <p className="mt-2 font-display text-[22px] uppercase">
                                                                Back to shop
                                                        </p>
                                                </div>
                                                <span className="font-mono text-[16px] text-[#FF9F1C] group-hover:translate-x-1 transition-transform">
                                                        →
                                                </span>
                                        </Link>
                                </div>

                                <div className="mt-12">
                                        <Link
                                                to="/account/logout"
                                                data-testid={ACCOUNT.indexSignOutButton}
                                                className="inline-block border border-white/25 font-mono text-[12px] tracking-[0.2em] uppercase px-5 py-3 hover:border-[#FF9F1C] hover:text-[#FF9F1C] transition-colors"
                                        >
                                                Sign out
                                        </Link>
                                </div>

                                <OrdersPanel id="orders-panel" />
                        </div>
                </main>
        );
}
